import { getAssetHost, drawsFor, type AssetHost, type RecordedDraw } from './assetProbe.js';

// The DSL's canvas probe — what a `defineSandboxTest` step's `assert` gets. Moved from @gotti/sandbox-test, which still
// declares `CanvasProbe` for the AI's type surface (a structural twin: `virtual.test-d.ts` proves each assignable to the other).

/**
 * Note: the asset assertions (expectAssetLoaded/expectSpriteDrawn) PREPEND a
 * custom `message` to their `[Asset:<path>]`-prefixed diagnostic detail
 * (unlike expectNotBlank, which replaces it) — the diagnostics feed the AI
 * fix loop and must never be suppressed by a custom message.
 */
export interface CanvasProbe {
  expectNotBlank(message?: string): void;
  expectColor(hex: string, opts?: { minPercent?: number; tolerance?: number }): void;
  drawnPixelCount(): number;
  /**
   * Strict surface check — mirrors the production e2e canvas gate exactly:
   * after quantizing each pixel to a 16-bucket-per-channel colour, the canvas
   * must show at least `minColors` distinct colours AND at least
   * `minDrawnPixels` pixels that are NOT the single dominant (background)
   * colour. A flat single-colour fill therefore FAILS — only a surface with a
   * visible, contrasting structure (e.g. a board/grid drawn on top of the
   * background) passes. Use this for the rendering test so "all tests pass"
   * actually entails a non-blank rendered surface.
   */
  expectSurfaceRendered(opts?: { minColors?: number; minDrawnPixels?: number; message?: string }): void;
  /**
   * Asserts the asset at `path` was preloaded into the AssetManager at boot.
   * Failure messages start with `[Asset:<path>]` and list what WAS loaded.
   * Throws `[Asset:<path>] asset probe unavailable in this runner` when the
   * host installed no AssetHost (never silently passes).
   */
  expectAssetLoaded(path: string, message?: string): void;
  /**
   * Asserts a ctx.drawImage call on THIS client's canvas drew the image asset
   * at `path` this run. `frame`: the draw's 9-arg source subrect must equal
   * the named frame's rect in the sheet's AnimationAtlas JSON (resolved via
   * the host; exact sx/sy/sw/sh compare). `near`: at least one matching draw's
   * destination (dx, dy) must be within `tolerance` px (default 1) of (x, y).
   * Failure messages start with `[Asset:<path>]` and describe what WAS drawn.
   */
  expectSpriteDrawn(path: string, opts?: { frame?: string; near?: { x: number; y: number; tolerance?: number }; message?: string }): void;
  /** Raw recorded draws for this canvas (diagnostics). */
  drawnSprites(): Array<{ assetPath: string | null; dx: number; dy: number }>;
  readonly canvas: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

/** "images/food.png (3 calls), (unknown image) (1 call)" — or a loud none. */
function summarizeDraws(draws: RecordedDraw[]): string {
  const counts = new Map<string, number>();
  for (const d of draws) {
    const key = d.assetPath ?? '(unknown image)';
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  if (counts.size === 0) return '(none — no drawImage calls were recorded on this canvas this run)';
  return [...counts].map(([k, n]) => `${k} (${n} call${n === 1 ? '' : 's'})`).join(', ');
}

/**
 * Single source of the `[Asset:<path>]` failure prefix for the asset
 * assertions. The later fix loop CLUSTERS failures on this prefix and feeds
 * `detail` to the repair prompt, so a custom `message` is PREPENDED after the
 * prefix and the diagnostic detail is ALWAYS appended (unlike expectNotBlank,
 * where a custom message replaces the default).
 */
function assetFail(path: string, detail: string, message?: string): never {
  throw new Error(`[Asset:${path}] ${message ? `${message} — ${detail}` : detail}`);
}

/**
 * Asset assertions need the host-installed AssetHost (the runner owns the real
 * AssetManager). Missing host ⇒ THROW — a runner without asset support must
 * fail the assertion loudly, never silently pass it.
 */
function requireAssetHost(path: string): AssetHost {
  const host = getAssetHost();
  if (!host) assetFail(path, 'asset probe unavailable in this runner');
  return host;
}

export function makeCanvasProbe(canvas: HTMLCanvasElement | null): CanvasProbe {
  const ctx = (canvas?.getContext('2d', { willReadFrequently: true } as any) ?? null) as CanvasRenderingContext2D | null;
  const readPixels = (): Uint8ClampedArray | null => {
    if (!canvas || !ctx) return null;
    return ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  };
  const drawnPixelCount = (): number => {
    const d = readPixels();
    if (!d) return 0;
    let n = 0;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i + 3] !== 0 && (d[i] || d[i + 1] || d[i + 2])) n++;
    }
    return n;
  };
  return {
    canvas: canvas as HTMLCanvasElement,
    ctx: ctx as CanvasRenderingContext2D,
    drawnPixelCount,
    expectNotBlank(message?: string): void {
      if (drawnPixelCount() === 0) {
        throw new Error(message ?? 'Expected canvas to not be blank, but nothing was drawn');
      }
    },
    expectColor(hex: string, opts?: { minPercent?: number; tolerance?: number }): void {
      const d = readPixels();
      if (!d) throw new Error(`Expected color ${hex} but no canvas was available`);
      const [r, g, b] = hexToRgb(hex);
      const tol = opts?.tolerance ?? 12;
      let match = 0, total = 0;
      for (let i = 0; i < d.length; i += 4) {
        total++;
        if (Math.abs(d[i] - r) <= tol && Math.abs(d[i + 1] - g) <= tol && Math.abs(d[i + 2] - b) <= tol) match++;
      }
      const pct = total ? (match / total) * 100 : 0;
      const min = opts?.minPercent ?? 0.5;
      if (pct < min) throw new Error(`Expected color ${hex} on >=${min}% of canvas, found ${pct.toFixed(2)}%`);
    },
    expectSurfaceRendered(opts?: { minColors?: number; minDrawnPixels?: number; message?: string }): void {
      const minColors = opts?.minColors ?? 2;
      const minDrawn = opts?.minDrawnPixels ?? 100;
      const d = readPixels();
      if (!d) {
        throw new Error(opts?.message ?? 'Expected a rendered surface but no canvas was available');
      }
      // Quantize to 16 buckets/channel — identical to the production e2e gate.
      const counts = new Map<string, number>();
      const totalPixels = d.length / 4;
      for (let i = 0; i < d.length; i += 4) {
        const key = `${d[i] >> 4},${d[i + 1] >> 4},${d[i + 2] >> 4}`;
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
      let dominant = 0;
      for (const n of counts.values()) if (n > dominant) dominant = n;
      const uniqueColors = counts.size;
      const nonDominant = totalPixels - dominant;
      if (uniqueColors < minColors || nonDominant < minDrawn) {
        throw new Error(
          opts?.message ??
            `Expected a rendered surface with >=${minColors} colors and >=${minDrawn} non-background pixels, ` +
              `but found ${uniqueColors} color(s) and ${nonDominant} non-background pixel(s). ` +
              `A flat single-color fill does not count — draw the static surface (board/grid) on top of the background.`,
        );
      }
    },
    expectAssetLoaded(path: string, message?: string): void {
      const host = requireAssetHost(path);
      if (host.has(path)) return;
      const loaded = host.list();
      const detail =
        `expected the asset to be loaded at boot but it was not. ` +
        `Loaded assets: ${loaded.length ? loaded.join(', ') : '(none)'}.`;
      assetFail(path, detail, message);
    },
    expectSpriteDrawn(
      path: string,
      opts?: { frame?: string; near?: { x: number; y: number; tolerance?: number }; message?: string },
    ): void {
      const host = requireAssetHost(path);
      // Always include the precise diagnostic — the fix loop clusters on the
      // `[Asset:<path>]` prefix and feeds the detail to the repair prompt.
      const fail: (detail: string) => never = (detail) => assetFail(path, detail, opts?.message);
      const draws = drawsFor(canvas);
      let candidates = draws.filter((d) => d.assetPath === path);
      if (candidates.length === 0) {
        const drawn = summarizeDraws(draws);
        if (!host.has(path)) {
          const loaded = host.list();
          fail(
            `expected the sprite to be drawn but the asset is NOT loaded. ` +
              `Loaded assets: ${loaded.length ? loaded.join(', ') : '(none)'}. Drawn assets: ${drawn}.`,
          );
        }
        fail(
          `expected the sprite to be drawn but no drawImage call used it this run. Drawn assets: ${drawn}. ` +
            (host.wasAccessed(path)
              ? `The asset IS loaded and WAS read via the AssetManager — check the render system's draw conditions.`
              : `The asset IS loaded — check the render system's draw path / asset path string.`),
        );
      }
      if (opts?.frame !== undefined) {
        const frame = opts.frame;
        const rect = host.resolveFrame(path, frame);
        if (!rect) {
          const frames = host.listFrames?.(path);
          fail(
            `frame "${frame}" is unknown for this sheet` +
              (frames && frames.length
                ? ` — known frames: ${frames.join(', ')}.`
                : ` — no AnimationAtlas whose "image" is this sheet declares it (check data/atlas/*.json).`),
          );
        }
        const frameMatches = candidates.filter(
          (d) => d.sx === rect.x && d.sy === rect.y && d.sw === rect.w && d.sh === rect.h,
        );
        if (frameMatches.length === 0) {
          const used = candidates
            .map((d) => (d.sx === null ? '(no source subrect — 3/5-arg draw)' : `(sx=${d.sx}, sy=${d.sy}, sw=${d.sw}, sh=${d.sh})`))
            .join(', ');
          fail(
            `expected frame "${frame}" (sx=${rect.x}, sy=${rect.y}, sw=${rect.w}, sh=${rect.h}) to be drawn, ` +
              `but the ${candidates.length} draw(s) of this sheet used: ${used}.`,
          );
        }
        candidates = frameMatches;
      }
      if (opts?.near) {
        const { x, y } = opts.near;
        const tol = opts.near.tolerance ?? 1;
        const nearMatches = candidates.filter((d) => Math.abs(d.dx - x) <= tol && Math.abs(d.dy - y) <= tol);
        if (nearMatches.length === 0) {
          const dests = candidates.map((d) => `(dx=${d.dx}, dy=${d.dy})`).join(', ');
          fail(
            `expected a draw of the sprite within ${tol}px of (${x}, ${y}), ` +
              `but its ${candidates.length} matching draw(s) landed at: ${dests}.`,
          );
        }
      }
    },
    drawnSprites(): Array<{ assetPath: string | null; dx: number; dy: number }> {
      // Diagnostics accessor, NOT an asset assertion — no `[Asset:` prefix
      // here: a pseudo-path like `[Asset:*]` would form a junk cluster in the
      // fix loop's failure clustering.
      if (!getAssetHost()) throw new Error('asset probe unavailable in this runner (drawnSprites)');
      return drawsFor(canvas).map((d) => ({ assetPath: d.assetPath, dx: d.dx, dy: d.dy }));
    },
  };
}
